import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { prefetchForPath } from "@/utils/prefetchRoutes";
import { trackPageView, startHeartbeat } from "@/utils/analytics";

// Route guard (small, kept eager)
import ProtectedVenueRoute from "@/components/ProtectedVenueRoute";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import CookieBanner from "@/components/CookieBanner";
import InstallPrompt from "@/components/InstallPrompt";

// Public pages — lazy-loaded so each route ships as its own chunk.
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PublicPricing = lazy(() => import("@/pages/PublicPricing"));
const ForVenues = lazy(() => import("@/pages/ForVenues"));
const ForOrganisers = lazy(() => import("@/pages/ForOrganisers"));
const ForEventGoers = lazy(() => import("@/pages/ForEventGoers"));
const ForInvytPro = lazy(() => import("@/pages/ForInvytPro"));
const FoundingPartners = lazy(() => import("@/pages/FoundingPartners"));
const GetStarted = lazy(() => import("@/pages/GetStarted"));
const CreateEvent = lazy(() => import("@/pages/CreateEvent"));
const EventWizard = lazy(() => import("@/pages/EventWizard"));
const VenueRegister = lazy(() => import("@/pages/VenueRegister"));
const VenueAdminLogin = lazy(() => import("@/pages/VenueAdminLogin"));
const SuperAdminLogin = lazy(() => import("@/pages/SuperAdminLogin"));
const SuperAdminDashboard = lazy(() => import("@/pages/SuperAdminDashboard"));
const ConfirmEmail = lazy(() => import("@/pages/ConfirmEmail"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const EventPublicRegister = lazy(() => import("@/pages/EventPublicRegister"));
const Discovery = lazy(() => import("@/pages/Discovery"));
const Music = lazy(() => import("@/pages/Music"));

// Venue-specific pages
const VenueHome = lazy(() => import("@/pages/venue/VenueHome"));
const VenueSignIn = lazy(() => import("@/pages/venue/VenueSignIn"));
const VenueSignOut = lazy(() => import("@/pages/venue/VenueSignOut"));
const StaffSignIn = lazy(() => import("@/pages/venue/StaffSignIn"));
const VenueUserDashboard = lazy(() => import("@/pages/venue/VenueUserDashboard"));
const VenueAdminPinLogin = lazy(() => import("@/pages/venue/VenueAdminPinLogin"));
const VenueAdminDashboard = lazy(() => import("@/pages/venue/VenueAdminDashboard"));
const VenueAdminHistory = lazy(() => import("@/pages/venue/VenueAdminHistory"));
const VenueSettings = lazy(() => import("@/pages/venue/VenueSettings"));
const VenueCollections = lazy(() => import("@/pages/venue/VenueCollections"));
const VenueBilling = lazy(() => import("@/pages/venue/VenueBilling"));
const VenueTeam = lazy(() => import("@/pages/venue/VenueTeam"));
const VenueAnalytics = lazy(() => import("@/pages/venue/VenueAnalytics"));
const VenueRollCall = lazy(() => import("@/pages/venue/VenueRollCall"));
const VenueKiosk = lazy(() => import("@/pages/venue/VenueKiosk"));

// Event Mode pages
const VenueEvents = lazy(() => import("@/pages/venue/VenueEvents"));
const VenueEventForm = lazy(() => import("@/pages/venue/VenueEventForm"));
const VenueEventWizard = lazy(() => import("@/pages/venue/VenueEventWizard"));
const VenueEventDetail = lazy(() => import("@/pages/venue/VenueEventDetail"));
const EventQRScanner = lazy(() => import("@/pages/venue/EventQRScanner"));
const EventRollCall = lazy(() => import("@/pages/venue/EventRollCall"));
const DoorStaffScanner = lazy(() => import("@/pages/event/DoorStaffScanner"));
const EventTicketPurchase = lazy(() => import("@/pages/event/EventTicketPurchase"));

// Event Organizer pages
const OrganizerSignup = lazy(() => import("@/pages/organizer/OrganizerSignup"));
const OrganizerDashboard = lazy(() => import("@/pages/organizer/OrganizerDashboard"));
const OrganizerCreateEvent = lazy(() => import("@/pages/organizer/OrganizerCreateEvent"));
const PublicEventEdit = lazy(() => import("@/pages/event/PublicEventEdit"));

// Attendee (Event-Goer) pages — named exports mapped to default for lazy().
const AttendeeLogin = lazy(() => import("@/pages/attendee/AttendeeAuth").then((m) => ({ default: m.AttendeeLogin })));
const AttendeeSignup = lazy(() => import("@/pages/attendee/AttendeeAuth").then((m) => ({ default: m.AttendeeSignup })));
const AuthCallback = lazy(() => import("@/pages/attendee/AttendeeAuth").then((m) => ({ default: m.AuthCallback })));
const AttendeeForgotPassword = lazy(() => import("@/pages/attendee/AttendeePasswordReset").then((m) => ({ default: m.AttendeeForgotPassword })));
const AttendeeResetPassword = lazy(() => import("@/pages/attendee/AttendeePasswordReset").then((m) => ({ default: m.AttendeeResetPassword })));
const AttendeeDashboard = lazy(() => import("@/pages/attendee/AttendeeDashboard"));
const InvytIdPage = lazy(() => import("@/pages/attendee/InvytIdPage"));
const WorkPasses = lazy(() => import("@/pages/attendee/WorkPasses"));
const WorkPassCheckin = lazy(() => import("@/pages/WorkPassCheckin"));
const VisitorCheckin = lazy(() => import("@/pages/VisitorCheckin"));
const StaffInvitePage = lazy(() => import("@/pages/attendee/StaffInvitePage"));
const PublicProfile = lazy(() => import("@/pages/attendee/PublicProfile"));
const VenueInviteAccept = lazy(() => import("@/pages/venue/VenueInviteAccept"));
const GoLive = lazy(() => import("@/pages/live/GoLive"));
const GlassLab = lazy(() => import("@/pages/GlassLab"));
const LiveStreamView = lazy(() => import("@/pages/live/LiveStreamView"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loading">
      <img
        src="/brand/invyt-loader.gif"
        alt="Loading Invyt"
        className="w-40 sm:w-48 h-auto object-contain select-none"
        draggable="false"
      />
    </div>
  );
}

// Auth callback handler - checks for session_id in URL hash
function AppRouter() {
  const location = useLocation();

  // Warm likely-next route chunks during idle time (lightweight prefetch).
  useEffect(() => {
    prefetchForPath(location.pathname);
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => { startHeartbeat(); }, []);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with ProtectedRoute checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PublicPricing />} />
      <Route path="/for-venues" element={<ForVenues />} />
      <Route path="/for-organisers" element={<ForOrganisers />} />
      <Route path="/for-event-goers" element={<ForEventGoers />} />
      <Route path="/invyt-pro" element={<ForInvytPro />} />
      <Route path="/founding-partners" element={<FoundingPartners />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/discover" element={<Discovery />} />
      <Route path="/music" element={<Music />} />
      <Route path="/get-started" element={<GetStarted />} />
      <Route path="/create-event" element={<EventWizard />} />
      <Route path="/create-event-legacy" element={<CreateEvent />} />
      <Route path="/register" element={<VenueRegister />} />
      <Route path="/venue-login" element={<VenueAdminLogin />} />
      <Route path="/super-admin" element={<SuperAdminLogin />} />
      <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Attendee (Event-Goer) routes */}
      <Route path="/attendee/login" element={<AttendeeLogin />} />
      <Route path="/attendee/signup" element={<AttendeeSignup />} />
      <Route path="/attendee/forgot-password" element={<AttendeeForgotPassword />} />
      <Route path="/attendee/reset-password" element={<AttendeeResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/my-events" element={<AttendeeDashboard />} />
      <Route path="/go-live" element={<GoLive />} />
      <Route path="/glass-lab" element={<GlassLab />} />
      <Route path="/live/:id" element={<LiveStreamView />} />
      <Route path="/venue-invite" element={<VenueInviteAccept />} />
      <Route path="/me/id" element={<InvytIdPage />} />
      <Route path="/work" element={<WorkPasses />} />
      <Route path="/wp/checkin/:venueSlug" element={<WorkPassCheckin />} />
      <Route path="/a/:venueSlug" element={<VisitorCheckin />} />
      <Route path="/staff/invite" element={<StaffInvitePage />} />
      <Route path="/profile/id" element={<InvytIdPage />} />
      {/* Public event-goer Creator Hub profile */}
      <Route path="/u/:username" element={<PublicProfile />} />
      {/* Public Event Registration */}
      <Route path="/events/:eventId/register" element={<EventPublicRegister />} />
      <Route path="/e/:eventSlug" element={<EventPublicRegister />} />
      <Route path="/preview-event" element={<EventPublicRegister />} />
      <Route path="/e/:eventSlug/edit" element={<PublicEventEdit />} />
      <Route path="/events/:eventId/tickets" element={<EventTicketPurchase />} />
      <Route path="/tickets/confirmation" element={<EventTicketPurchase />} />

      {/* Venue-specific routes */}
      {/* Public venue profile page (always accessible) */}
      <Route path="/organiser/:venueSlug" element={<VenueHome />} />
      <Route path="/:venueSlug" element={<VenueHome />} />

      {/* Admin PIN login is public so admins can unlock the device */}
      <Route path="/:venueSlug/admin/login" element={<VenueAdminPinLogin />} />

      {/* Kiosk-only routes — require an admin-unlocked device */}
      <Route path="/:venueSlug/sign-in" element={<ProtectedVenueRoute><VenueSignIn /></ProtectedVenueRoute>} />
      <Route path="/:venueSlug/sign-out" element={<ProtectedVenueRoute><VenueSignOut /></ProtectedVenueRoute>} />
      <Route path="/:venueSlug/staff-sign-in" element={<ProtectedVenueRoute><StaffSignIn /></ProtectedVenueRoute>} />
      <Route path="/:venueSlug/visitors" element={<ProtectedVenueRoute><VenueUserDashboard /></ProtectedVenueRoute>} />
      <Route path="/:venueSlug/kiosk" element={<ProtectedVenueRoute><VenueKiosk /></ProtectedVenueRoute>} />

      {/* Admin pages already enforce admin auth internally */}
      <Route path="/:venueSlug/admin/dashboard" element={<VenueAdminDashboard />} />
      <Route path="/:venueSlug/admin/history" element={<VenueAdminHistory />} />
      <Route path="/:venueSlug/admin/settings" element={<VenueSettings />} />
      <Route path="/:venueSlug/admin/collections" element={<VenueCollections />} />
      <Route path="/:venueSlug/admin/billing" element={<VenueBilling />} />
      <Route path="/:venueSlug/admin/team" element={<VenueTeam />} />
      <Route path="/:venueSlug/admin/analytics" element={<VenueAnalytics />} />
      <Route path="/:venueSlug/admin/roll-call" element={<VenueRollCall />} />

      {/* Event Mode routes */}
      <Route path="/:venueSlug/admin/events" element={<VenueEvents />} />
      <Route path="/:venueSlug/admin/events/new" element={<VenueEventWizard />} />
      <Route path="/:venueSlug/admin/events/:eventId" element={<VenueEventDetail />} />
      <Route path="/:venueSlug/admin/events/:eventId/edit" element={<VenueEventWizard />} />
      <Route path="/:venueSlug/admin/events/:eventId/edit-legacy" element={<VenueEventForm />} />
      <Route path="/:venueSlug/admin/events/:eventId/scan" element={<EventQRScanner />} />
      <Route path="/:venueSlug/admin/events/:eventId/rollcall" element={<EventRollCall />} />
      <Route path="/:venueSlug/admin/events/:eventId/door-staff" element={<DoorStaffScanner />} />

      {/* Event Organizer routes */}
      <Route path="/:venueSlug/host-event" element={<OrganizerSignup />} />
      <Route path="/:venueSlug/organizer/dashboard" element={<OrganizerDashboard />} />
      <Route path="/:venueSlug/organizer/create-event" element={<OrganizerCreateEvent />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-slate-50">
      <BrowserRouter>
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <AppRouter />
          </Suspense>
        </ChunkErrorBoundary>
        <CookieBanner />
        <InstallPrompt />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
